import { create } from 'zustand';
import apiClient from '../lib/api';

export interface WhiteboardElement {
    id: string;
    type: 'rect' | 'circle' | 'line' | 'text' | 'sticky';
    x: number;
    y: number;
    width?: number;
    height?: number;
    text?: string;
    color: string;
    owner: string;
    lastUpdated: string;
}

interface WhiteboardState {
    boardId: string | null;
    boardName: string;
    elements: WhiteboardElement[];
    projectId: string | null;
    isLoading: boolean;
    isSaving: boolean;
    setProjectId: (id: string) => void;
    addElement: (element: Omit<WhiteboardElement, 'id' | 'lastUpdated'>) => void;
    updateElement: (id: string, updates: Partial<WhiteboardElement>) => void;
    deleteElement: (id: string) => void;
    loadBoard: () => Promise<void>;
    fetchElements: (projectId: string) => Promise<void>;
    saveNow: () => Promise<void>;
}

function parseContent(raw: any): WhiteboardElement[] {
    const c = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return {}; } })() : (raw || {});
    return Array.isArray(c?.elements) ? c.elements : [];
}

// Debounced auto-save so every drag/edit doesn't hammer the API.
let saveTimer: ReturnType<typeof setTimeout> | null = null;

export const useWhiteboardStore = create<WhiteboardState>((set, get) => {
    const schedulePersist = () => {
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(() => { get().saveNow(); }, 600);
    };

    return {
        boardId: null,
        boardName: '',
        elements: [],
        projectId: null,
        isLoading: false,
        isSaving: false,

        setProjectId: (id) => set({ projectId: id }),

        addElement: (element) => {
            const newElement: WhiteboardElement = {
                ...element,
                id: (crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2, 11)),
                lastUpdated: new Date().toISOString(),
            };
            set(state => ({ elements: [...state.elements, newElement] }));
            schedulePersist();
        },

        updateElement: (id, updates) => {
            set(state => ({
                elements: state.elements.map(el =>
                    el.id === id ? { ...el, ...updates, lastUpdated: new Date().toISOString() } : el
                )
            }));
            schedulePersist();
        },

        deleteElement: (id) => {
            set(state => ({ elements: state.elements.filter(el => el.id !== id) }));
            schedulePersist();
        },

        // Loads (or creates) the workspace whiteboard and hydrates its saved elements.
        loadBoard: async () => {
            set({ isLoading: true });
            try {
                const res = await apiClient.get('/work/whiteboards');
                const boards = res.data?.data ?? res.data ?? [];
                let board = Array.isArray(boards) && boards.length > 0 ? boards[0] : null;
                if (!board) {
                    const created = await apiClient.post('/work/whiteboards', {
                        name: 'Team Whiteboard',
                        content: { elements: [] },
                    });
                    board = created.data?.data ?? created.data;
                }
                set({
                    boardId: board.id,
                    boardName: board.name,
                    elements: parseContent(board.content),
                    isLoading: false,
                });
            } catch {
                set({ isLoading: false });
            }
        },

        // Back-compat with the screen's existing call; project scoping is optional.
        fetchElements: async (projectId) => {
            set({ projectId });
            await get().loadBoard();
        },

        saveNow: async () => {
            const { boardId, elements } = get();
            if (!boardId) return;
            set({ isSaving: true });
            try {
                await apiClient.put(`/work/whiteboards/${boardId}/content`, { content: { elements } });
            } catch {
                /* best-effort; will retry on next change */
            } finally {
                set({ isSaving: false });
            }
        },
    };
});
